
import { ErrorBoundary } from 'react-error-boundary';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '@/hooks/use-mobile';

// Import direct - pas de lazy loading pour éviter les erreurs de chunk sur mobile
import SchedulePageComponent from './schedule/SchedulePage';

// Fallback when there's an error loading the schedule
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) => {
  const { t } = useTranslation('common');
  const isMobile = useIsMobile();
  
  const isChunkError = error.message?.includes('module') || 
                       error.message?.includes('chunk') ||
                       error.message?.includes('import') ||
                       error.message?.includes('Failed to fetch');
  
  const handleReload = () => {
    sessionStorage.removeItem('chunk_reload_count');
    window.location.reload();
  };
  
  return (
    <Card className="w-full">
      <CardContent className={isMobile ? "pt-4 p-4" : "pt-6"}>
        <Alert variant="destructive">
          <AlertCircle className={isMobile ? "h-3 w-3" : "h-4 w-4"} />
          <AlertTitle className={isMobile ? "text-xs" : ""}>
            {isChunkError 
              ? t('loadingError', { defaultValue: 'Erreur de chargement' }) 
              : t('error', { defaultValue: 'Erreur' })}
          </AlertTitle>
          <AlertDescription className={isMobile ? "text-[10px]" : ""}>
            {isChunkError 
              ? t('chunkLoadFailed', { defaultValue: 'Impossible de charger la page. Une nouvelle version est peut-être disponible.' })
              : error.message || t('somethingWentWrong', { defaultValue: 'Une erreur est survenue.' })}
          </AlertDescription>
        </Alert>
        <div className="mt-4 flex gap-2">
          <Button onClick={handleReload} variant="default" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('reload', { defaultValue: 'Recharger' })}
          </Button>
          <Button onClick={resetErrorBoundary} variant="outline" size="sm">
            {t('retry', { defaultValue: 'Réessayer' })}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Schedule = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <SchedulePageComponent />
    </ErrorBoundary>
  );
};

export default Schedule;
